import { useState, useEffect } from "react";
import Typography from "../../components/Typography/Typography";
import { LengthCard } from "../../components/LenghtCard/LenghtCard";
import Toggle from "../../components/Toggle/Toggle";
import Button from "../../components/Button/Button";
import { ArrowLeft, Check, FilePen, RefreshCw } from "lucide-react";
import ChapterSection from "./ChapterSection";
import ChapterList from "./AddSectionButton";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import ApiManager from "../../api/ApiManager";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const lengths = [
  { title: "Essenziale", subtitle: "3–4 capitoli" },
  { title: "Standard", subtitle: "5–6 capitoli" },
  { title: "Approfondita", subtitle: "7–8 capitoli" },
  { title: "Estesa", subtitle: "9+ capitoli" },
];

const strutture = [
  {
    title: "Classica",
    subtitle: "Introduzione, teoria, pratica e conclusioni.",
  },
  { title: "Tematica", subtitle: "Ogni capitolo tratta un tema specifico." },
  {
    title: "Cronologica",
    subtitle: "Ordina gli argomenti secondo una linea temporale.",
  },
];

const IndexComponent = ({
  hasEnoughDocs,
  loading,
  aiResponse,
  setAiResponse,
  handleGenerateIndex,
  handleGenerateNuovo,
  projectId,
  getCookie,
  api,
  indexId
}) => {
  const [selectedIndexLenght, setSelectedIndexLenght] = useState(1);
  const [selectedIndexStructure, setSelectedIndexStructure] = useState(0);
  const [hasSubsections, setHasSubsections] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);
  const [items, setItems] = useState([]);
  const [chapterItems, setChapterItems] = useState([]); // New state for chapter items
  const apix = new ApiManager();

  // Setup DnD sensors for both subchapters and chapters
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        tolerance: 5,
        delay: 250, // Aggiunge un piccolo ritardo per differenziare drag da scroll
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  // Update chapter and subchapter items when aiResponse changes
  useEffect(() => {
    if (aiResponse?.chapters) {
      // Generate IDs for subchapters
      const newItems = aiResponse.chapters.flatMap((chapter, chIdx) =>
        (chapter.subchapters || []).map((_, subIdx) => `chapter-${chIdx}-sub-${subIdx}`)
      );
      setItems(newItems);

      // Generate IDs for chapters
      const newChapterItems = aiResponse.chapters.map((_, idx) => `chapter-${idx}`);
      setChapterItems(newChapterItems);
    }
  }, [aiResponse]);

  // Function to update index after editing
  const updateIndex = async (updatedIndex) => {
    try {
      // Crea una copia pulita dell'indice aggiornato per evitare problemi con JSON.stringify
      const cleanedIndex = JSON.parse(JSON.stringify(updatedIndex));
      const updatedIndexString = JSON.stringify(cleanedIndex);

      console.log("Sending update to API:", updatedIndexString);

      return apix.put(
        "/index/update",
        {
          project_id: projectId,
          index_id: indexId,
          ai_response: updatedIndexString // Invia come stringa invece di oggetto JSON
        },
        { headers: { Authorization: `Bearer ${getCookie("id_token")}` } }
      )
        .then((response) => {
          toast.success("Indice aggiornato con successo!");
          console.log("Index updated successfully:", response.data);
          // Aggiorna lo stato locale
          setAiResponse(cleanedIndex);
          return response;
        })
        .catch((error) => {
          console.error("Error updating index:", error);
          throw error;
        });

    } catch (error) {
      console.error("Error preparing index update:", error);
      throw error;
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // Aggiungi questa riga per prevenire lo scroll durante il trascinamento
    document.body.style.overflow = 'hidden';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';

    // Handle drag start for subchapters
    if (active.id.toString().includes('sub-')) {
      const idParts = active.id.toString().split('-');
      const chapterIndex = parseInt(idParts[1]);
      const subchapterIndex = parseInt(idParts[3]);

      if (aiResponse.chapters[chapterIndex] &&
        aiResponse.chapters[chapterIndex].subchapters[subchapterIndex]) {
        setActiveDragData({
          type: 'subchapter',
          data: aiResponse.chapters[chapterIndex].subchapters[subchapterIndex],
          chapterIndex,
          subchapterIndex
        });
      }
    }
    // Handle drag start for chapters
    else if (active.id.toString().startsWith('chapter-')) {
      const chapterIndex = parseInt(active.id.toString().split('-')[1]);

      if (aiResponse.chapters[chapterIndex]) {
        setActiveDragData({
          type: 'chapter',
          data: aiResponse.chapters[chapterIndex],
          chapterIndex
        });
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Reset drag states
    setActiveId(null);
    setActiveDragData(null);

    // Ripristina lo scrolling
    document.body.style.userSelect = '';
    document.body.style.pointerEvents = '';
    document.body.style.overflow = '';
    // Se non c'è un elemento over, il drag è stato cancellato
    if (!over) return;

    console.log('Drag Ended:', { active, over });

    // Handle subchapter drag
    if (active.id.toString().includes('sub-') && over.id.toString().includes('sub-')) {
      try {
        // Estrai gli indici del capitolo e sottocapitolo dagli ID
        const activeIdParts = active.id.toString().split('-');
        const overIdParts = over.id.toString().split('-');

        const activeChapterIndex = parseInt(activeIdParts[1]);
        const activeSubchapterIndex = parseInt(activeIdParts[3]);
        const overChapterIndex = parseInt(overIdParts[1]);
        const overSubchapterIndex = parseInt(overIdParts[3]);

        if (!aiResponse?.chapters?.[activeChapterIndex]?.subchapters) return;

        console.log('Drag indices:', {
          activeChapterIndex,
          activeSubchapterIndex,
          overChapterIndex,
          overSubchapterIndex
        });

        // Se ci stiamo muovendo all'interno dello stesso capitolo
        if (activeChapterIndex === overChapterIndex && activeSubchapterIndex !== overSubchapterIndex) {
          // Crea una copia profonda dei capitoli per evitare riferimenti
          const updatedChapters = JSON.parse(JSON.stringify(aiResponse.chapters));
          const currentChapter = updatedChapters[activeChapterIndex];

          // Verifica che il capitolo e i sottocapitoli esistano
          if (!currentChapter || !currentChapter.subchapters) {
            console.error('Chapter or subchapters not found', { activeChapterIndex, currentChapter });
            return;
          }

          console.log('Before reordering:', currentChapter.subchapters);

          // Usa arrayMove di dnd-kit per riordinare i sottocapitoli
          const reorderedSubchapters = arrayMove(
            currentChapter.subchapters,
            activeSubchapterIndex,
            overSubchapterIndex
          );

          console.log('After reordering:', reorderedSubchapters);

          // Aggiorna il capitolo con i sottocapitoli riordinati
          updatedChapters[activeChapterIndex] = {
            ...currentChapter,
            subchapters: reorderedSubchapters
          };

          // Crea un nuovo oggetto response per forzare il re-render
          const updatedResponse = {
            ...aiResponse,
            chapters: updatedChapters
          };

          // Aggiorna lo stato e salva al backend
          console.log('Updating state with new order');
          setAiResponse(updatedResponse);

          // Chiamata API per salvare i cambiamenti
          console.log('Calling updateIndex with new order');
          updateIndex(updatedResponse);

          console.log(`Subchapter moved from index ${activeSubchapterIndex} to index ${overSubchapterIndex} in chapter ${activeChapterIndex + 1}`);
        }
      } catch (error) {
        console.error('Error in handleDragEnd for subchapters:', error);
      }
    }
    // Handle chapter drag
    else if (
      active.id.toString().startsWith('chapter-') &&
      !active.id.toString().includes('sub-') &&
      over.id.toString().startsWith('chapter-') &&
      !over.id.toString().includes('sub-')
    ) {
      try {
        const activeChapterIndex = parseInt(active.id.toString().split('-')[1]);
        const overChapterIndex = parseInt(over.id.toString().split('-')[1]);

        if (activeChapterIndex !== overChapterIndex) {
          // Create a deep copy of the chapters
          const updatedChapters = JSON.parse(JSON.stringify(aiResponse.chapters));

          // Reorder chapters
          const reorderedChapters = arrayMove(
            updatedChapters,
            activeChapterIndex,
            overChapterIndex
          );

          console.log('Chapters reordered:', {
            from: activeChapterIndex,
            to: overChapterIndex,
            result: reorderedChapters.map(ch => ch.title)
          });

          // Create updated response
          const updatedResponse = {
            ...aiResponse,
            chapters: reorderedChapters
          };

          // Update state and save to backend
          setAiResponse(updatedResponse);
          updateIndex(updatedResponse);
        }
      } catch (error) {
        console.error('Error in handleDragEnd for chapters:', error);
      }
    }
  };

  // Dragged item visualization components
  const DraggedSubchapter = ({ data, displayNumber }) => {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border-2 border-accent">
        <p className="text-black font-medium">
          {displayNumber}{data.title}
        </p>
        {data.subtitle && <p className="text-gray-600 text-sm">{data.subtitle}</p>}
      </div>
    );
  };

  const DraggedChapter = ({ data, displayNumber }) => {
    return (
      <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-accent w-full max-w-md">
        <h2 className="text-xl font-semibold text-black">
          {`${displayNumber}. ${data.title}`}
        </h2>
        {data.subtitle && <p className="text-gray-600 mb-2">{data.subtitle}</p>}
        {data.subchapters && data.subchapters.length > 0 && (
          <div className="pl-4 border-l-2 border-gray-150 ml-2 mt-2">
            <p className="text-gray-500 text-sm">{data.subchapters.length} paragrafi</p>
          </div>
        )}
      </div>
    );
  };

  // In IndexComponent.jsx
  const removeChapterFunction = (chapterIndex) => {
    console.log("Removing chapter at index:", chapterIndex);
    if (!aiResponse || !aiResponse.chapters) return;

    // Create a copy of the current chapters
    const updatedChapters = [...aiResponse.chapters];

    // Remove the chapter from the list
    updatedChapters.splice(chapterIndex, 1);

    // Create a new response object with updated chapters
    const updatedResponse = {
      ...aiResponse,
      chapters: updatedChapters
    };

    // Update local state with the new array of chapters
    setAiResponse(updatedResponse);

    // Send the update to the backend
    updateIndex(updatedResponse);
  };

  // Add subchapter to specified chapter
  const addSubchapter = (chapterIndex) => {
    if (!aiResponse || !aiResponse.chapters) return;

    const newSubchapter = {
      title: "Inserisci il titolo del tuo sottoparagrafo",
      subtitle: "Una breve descrizione va qua"
    };

    const updatedChapters = [...aiResponse.chapters];

    // Initialize subchapters array if it doesn't exist
    if (!updatedChapters[chapterIndex].subchapters) {
      updatedChapters[chapterIndex].subchapters = [];
    }

    updatedChapters[chapterIndex].subchapters.push(newSubchapter);

    const updatedResponse = {
      ...aiResponse,
      chapters: updatedChapters
    };

    setAiResponse(updatedResponse);
    updateIndex(updatedResponse);
  };

  // Update chapter title or subtitle
  const updateChapter = (chapterIndex, field, value) => {
    if (!aiResponse || !aiResponse.chapters) return;

    const updatedChapters = [...aiResponse.chapters];
    updatedChapters[chapterIndex][field] = value;

    const updatedResponse = {
      ...aiResponse,
      chapters: updatedChapters
    };

    setAiResponse(updatedResponse);
    // Save changes only when exiting edit mode to avoid excessive API calls
  };

  // Update subchapter title or subtitle
  const updateSubchapter = (chapterIndex, subchapterIndex, field, value) => {
    if (!aiResponse || !aiResponse.chapters) return;

    const updatedChapters = [...aiResponse.chapters];
    updatedChapters[chapterIndex].subchapters[subchapterIndex][field] = value;

    const updatedResponse = {
      ...aiResponse,
      chapters: updatedChapters
    };

    setAiResponse(updatedResponse);
    // Save changes only when exiting edit mode to avoid excessive API calls
  };

  const renderIndexView = () => {
    if (!aiResponse) {
      return renderFormView();
    }

    return (
      <div className="space-y-8">
        <div className="flex flex-col gap-4">
          {/* Titolo e sottotitolo */}
          <Typography
            size="36"
            desktopSize="48"
            family="serif"
            color="black"
            weight="sb"
          >
            {aiResponse?.title || "Indice della tesi"}
          </Typography>

          <Typography size="20" desktopSize="24" color="gray-600" weight="md">
            {aiResponse?.subtitle || ""}
          </Typography>
        </div>

        <div className="h-[2px] bg-gray-150 w-full" />

        {/* Capitoli */}
        <div className="space-y-6 mt-8">
          {console.log("removeChapterFunction:", removeChapterFunction)} {/* Log the function once outside JSX */}
          {isEditMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={chapterItems} strategy={verticalListSortingStrategy}>
                <div className="space-y-6">
                  {/* Render ChapterList here, which already renders the chapters */}
                  <ChapterList
                    initialChapters={aiResponse.chapters}
                    onChaptersChange={(updatedChapters) =>
                      setAiResponse((prev) => ({ ...prev, chapters: updatedChapters }))
                    }
                    removeChapterFunction={removeChapterFunction}
                  />
                </div>

                {/* Nested SortableContext for subchapters */}
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                  {/* This is empty because subchapters are rendered within chapters */}
                </SortableContext>
              </SortableContext>

              {/* Overlay for dragged element */}
              <DragOverlay adjustScale={true}>
                {activeId && activeDragData?.type === "subchapter" && (
                  <DraggedSubchapter
                    data={activeDragData.data}
                    displayNumber={`${activeDragData.chapterIndex + 1}.${activeDragData.subchapterIndex + 1}`}
                  />
                )}
                {activeId && activeDragData?.type === "chapter" && (
                  <DraggedChapter
                    data={activeDragData.data}
                    displayNumber={activeDragData.chapterIndex + 1}
                  />
                )}
              </DragOverlay>
            </DndContext>
          ) : (
            aiResponse?.chapters?.map((chapter, chapterIndex) => {
              console.log("removeChapterFunction inside map:", removeChapterFunction); // Log inside the map

              return (
                <div key={`chapter-${chapterIndex}`}>
                  {/* Pass removeChapter correctly */}
                  <ChapterSection
                    chapter={chapter}
                    chapterIndex={chapterIndex}
                    isEditMode={false}
                    updateChapter={updateChapter}
                    updateSubchapter={updateSubchapter}
                    addSubchapter={addSubchapter}
                  />
                </div>
              );
            })
          )}
        </div>

        <div className="w-full flex justify-center">
          <img
            src="/assets/images/illustrations/end_index.png"
            alt="Treasure Chest"
            className="w-64 mx-auto -mb-14 md:-mb-2" // oppure mb-[-2.5rem]
          />
        </div>
      </div>
    );
  };

  // Modifica anche il toggleEditMode per salvare e inviare le modifiche
  const toggleEditMode = () => {
    if (isEditMode) {
      // Se stiamo uscendo dalla modalità modifica, salviamo le modifiche
      updateIndex(aiResponse);
    }
    setIsEditMode(!isEditMode);
  };

  const renderFormView = () => {
    return (
      <div className="flex flex-col gap-6 md:gap-9 md:w-[40rem] items-center justify-center pb-24">
        <div className="flex flex-col gap-3">
          <Typography
            size="32"
            desktopSize="48"
            family="serif"
            weight="sb"
            align="center"
          >
            Prepariamo insieme la struttura della tua tesi
          </Typography>
          <Typography
            size="16"
            desktopSize="20"
            weight="md"
            align="center"
            color="gray-600"
          >
            In base al tipo di tesi e all'argomento che hai scelto, ti guideremo
            nella creazione di un indice personalizzato.
          </Typography>
        </div>

        <div className="flex flex-col gap-3 w-full">
          {/* Lunghezza indice */}
          <div className="flex flex-col gap-3 bg-gray-100 p-5 border-2 border-gray-150 rounded-[8px]">
            <div className="flex flex-col gap-1">
              <Typography weight="sb" size="16">
                Quanti capitoli vorresti includere nella tua tesi?
              </Typography>
              <Typography size="12" weight="md" color="gray-600">
                Non preoccuparti, potrai sempre modificare la struttura in
                seguito.
              </Typography>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-4">
              {lengths.map((option, index) => (
                <LengthCard
                  key={option.title}
                  title={option.title}
                  subtitle={option.subtitle}
                  selected={selectedIndexLenght === index}
                  onClick={() => setSelectedIndexLenght(index)}
                />
              ))}
            </div>
          </div>

          {/* Sottosezioni */}
          <div className="flex justify-between items-center bg-gray-100 p-5 border-2 border-gray-150 rounded-[8px]">
            <div className="flex flex-col gap-1">
              <Typography weight="sb" size="16">
                Vuoi aggiungere delle sottosezioni?
              </Typography>
              <Typography size="12" weight="md" color="gray-600">
                Usare almeno un livello di sottocapitoli aiuta ad organizzare i
                contenuti.
              </Typography>
            </div>
            <div>
              <Toggle
                id="sottosezioni"
                checked={hasSubsections}
                onChange={() => setHasSubsections(!hasSubsections)}
              />
            </div>
          </div>

          {/* Struttura indice */}
          <div className="flex flex-col gap-3 bg-gray-100 p-5 border-2 border-gray-150 rounded-[8px]">
            <div className="flex flex-col gap-1">
              <Typography weight="sb" size="16">
                Che tipo di struttura preferisci per la tua tesi?
              </Typography>
              <Typography size="12" weight="md" color="gray-600">
                Una buona struttura ti aiuta a mantenere coerenza e chiarezza
                durante la scrittura.
              </Typography>
            </div>
            <div className="flex md:flex-row flex-col gap-2">
              {strutture.map((option, index) => (
                <LengthCard
                  key={option.title}
                  title={option.title}
                  subtitle={option.subtitle}
                  selected={selectedIndexStructure === index}
                  onClick={() => setSelectedIndexStructure(index)}
                />
              ))}
            </div>
          </div>
        </div>

        <Button
          size="md"
          variant="primary"
          width="full"
          text="Genera ora il tuo indice"
          onClick={handleGenerateIndex}
        />
      </div>
    );
  };

  const renderLoadingView = () => {
    return (
      <div className="flex flex-col gap-3 w-full pb-24">
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-28" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-14" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-52" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-52" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-52" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-52" />
        <div className="rounded-[12px] bg-gray-100 animate-pulse h-52" />
      </div>
    );
  };

  const renderNotEnoughDocsView = () => {
    return (
      <>
        <div className="flex flex-col gap-3">
          <Typography
            size="36"
            desktopSize="48"
            family="serif"
            weight="sb"
            align="center"
          >
            Ci sei quasi!
          </Typography>
          <div className="flex flex-col gap-2 justify-center items-center">
            <Typography size="16" desktopSize="20" weight="md" align="center">
              Per generare il tuo indice, aggiungi alla tua libreria almeno 5
              documenti!
            </Typography>
            <Link to="/client/library">
              <Typography
                size="16"
                desktopSize="20"
                weight="sb"
                align="center"
                underline={true}
              >
                Vai alla libreria
              </Typography>
            </Link>
          </div>
        </div>

        <div className="h-96 w-auto overflow-hidden">
          <img
            alt="placeholderimage"
            src="/assets/images/illustrations/above_clouds.png"
            className="object-contain w-full h-full"
          />
        </div>
      </>
    );
  };

  // Bottom toolbar component based on the images
  const renderBottomToolbar = () => {
    if (!aiResponse) return null;
    const isMobile = window.innerWidth < 768;

    return (
      <div className="w-full sticky bottom-0 md:py-6 py-4 flex justify-center bg-gray-100 border-t-2 border-gray-150 z-50">
        <div className="w-full flex justify-between md:px-[120px] px-4">
          <Button
            ricon={<RefreshCw size={20} />}
            onClick={handleGenerateNuovo}
            size={isMobile ? "sm" : "md"}
            type="bw"
            text="Rigenera"
          />
          <Button
            licon={isEditMode ? <Check size={20} /> : <FilePen size={20} />}
            text={isEditMode ? "Salva" : "Modifica"}
            type="color"
            size={isMobile ? "sm" : "md"}
            onClick={toggleEditMode}
          />
        </div>
      </div>
    );
  };

  // Determine what to render
  const renderContent = () => {
    if (loading) {
      return renderLoadingView();
    }

    // Check if aiResponse exists and has actual content
    if (aiResponse && (
      (aiResponse.chapters && aiResponse.chapters.length > 0) ||
      (aiResponse.title && aiResponse.thesis_info)
    )) {
      return renderIndexView();
    }

    if (!hasEnoughDocs) {
      return renderNotEnoughDocsView();
    }

    if (hasEnoughDocs && aiResponse === null) {
      return renderFormView();
    }
  };

  return (
    <>
      <Toaster />

      <div className="w-full flex flex-col items-center px-6 lg:px-[120px] pt-10 pb-12 md:pb-0 justify-between">
        {renderContent()}
      </div>
      {renderBottomToolbar()}
    </>
  );
};

export default IndexComponent;